import { useContext, useMemo } from "react";
import tinycolor from 'tinycolor2';
import {
    Box,
    Typography,
    FormGroup,
    MenuItem,
    FormControl,
    Select
} from "@mui/material";
import { useSelector} from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { LineSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import CommonChartSettingsTabs from "./commonChartSettingsTabs";

type Props = {
    viewKey: string;
};

const AreaSettingsDialogs = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))

    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])
    
    const priceSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.source ?? "close"
        }
        return "close"
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const lineColor = useMemo(() => {
        let color = '#33D778'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.lineColor ?? '#33D778'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    
    const topColor = useMemo(() => {
        let color = 'rgba( 46, 220, 135, 0.4)'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.topColor ?? 'rgba( 46, 220, 135, 0.4)'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const bottomColor = useMemo(() => {
        let color = 'rgba( 40, 221, 100, 0)'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.bottomColor ?? 'rgba( 40, 221, 100, 0)'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    
    const handleColorChangeSeries = (newColor: string, type: 'lineColor' | 'topColor' | 'bottomColor') => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            });
        }
    };
    
    const handleSourceChange = (newSource: 'high' | 'close' | 'open' | 'low') => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                source: newSource
            } as LineSeriesOptions)
        }
    };

    const symbolTabContent = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormGroup>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Typography variant="body2">Price Source</Typography>
                    <FormControl size="small">
                        <Select
                            value={priceSettings}
                            onChange={(event) => handleSourceChange(event.target.value as 'open' | 'high' | 'low' | 'close')}
                        >
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="close">Close</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </FormGroup>
            <FormGroup>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography sx={{ width: 50, fontSize: "14px" }}>line</Typography>
                    <ColorSelector compact showOnlySwatch color={lineColor.toHexString()} opacity={lineColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'lineColor');
                        }} />
                </Box>
            </FormGroup>
            <FormGroup>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1, }}>
                    <Typography sx={{ width: 50, fontSize: "14px" }}>fill</Typography>
                    <ColorSelector compact showOnlySwatch color={topColor.toHexString()} opacity={topColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'topColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={bottomColor.toHexString()} opacity={bottomColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'bottomColor');
                        }} />
                </Box>
            </FormGroup>
        </Box>
    );

    return (
        <CommonChartSettingsTabs
            viewKey={viewKey}
            symbolTabContent={symbolTabContent}
        />
    );
};

export default AreaSettingsDialogs;
